<template>
  <div class="simul">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <div v-if="!isLoading">
      <div class="is-flex is-justify-content-space-between">
        <router-link to="/simuls" class="is-size-4 has-text-link has-text-left">
          &lt;&lt; Simuls
        </router-link>

        <p class="is-size-4 has-text-success-dark">
          {{
            simul.simulName ? simul.simulName : `${simul.owner.name}'s simul`
          }}
        </p>

        <p class="is-size-4 has-text-right">
          {{ simul.players ? simul.players.length : 0 }}/{{ simul.playerLimit }}
          players
        </p>
      </div>

      <SimulTable :simuls="[simul]" />

      <div
        class="is-flex is-justify-content-space-between mt-4"
        v-if="permission.admin || (permission.hostSimul && isOwner)"
      >
        <div class="is-flex is-justify-content-flex-start">
          <b-button
            class="mr-2"
            label="Invite"
            type="is-primary"
            icon-left="paper-plane"
            :disabled="!canAddPlayer"
            :loading="isLoading"
            @click="addPlayers"
          />
          <b-button
            class="mr-2"
            label="Start"
            type="is-success"
            icon-left="play"
            :disabled="!canStart"
            :loading="isLoading"
            @click="startSimul"
          />
          <b-button
            class="mr-2"
            label="Update"
            type="is-info"
            icon-left="edit"
            :disabled="!canUpdate"
            @click="updateSimul"
          />
          <b-button
            label="Close"
            type="is-danger"
            icon-left="power-off"
            :disabled="!canClose"
            :loading="isLoading"
            @click="closeSimul"
          />
        </div>

        <div class="is-flex is-justify-content-space-between">
          <div
            class="is-flex is-justify-content-flex-start"
            v-if="canAddPlayer"
          >
            <b-field
              label="Add a bot level"
              label-position="on-border"
              style="width: 150px"
            >
              <b-select
                placeholder="Select a bot level"
                v-model="aiLevel"
                expanded
              >
                <option
                  v-for="(_, idx) in Array(12)"
                  :value="idx + 1"
                  :key="idx + 1"
                >
                  {{ idx + 1 }}
                </option>
              </b-select>
            </b-field>

            <b-button
              class="ml-4"
              label="Add bot"
              type="is-primary is-light"
              :disabled="!canAddPlayer"
              @click="addAiPlayer"
            />
          </div>
          <div class="is-flex is-justify-content-flex-end" v-else>
            <b-notification
              has-icon
              type="is-danger"
              role="alert"
              :closable="false"
              size="is-small"
            >
              Maximum number of players reached
            </b-notification>
          </div>
        </div>
      </div>

      <SimulPlayersTable :simul="simul" :isOwner="isOwner" />

      <AddOrInvitePlayers
        v-if="!!simul"
        ref="addPlayers"
        :excluded="playerIds"
        @onInvite="invitePlayer"
        @onAdd="addPlayer"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import {
  GAME_OVERRIDE,
  SIMUL_STATUS,
  CHESS_ENGINE,
  CHESS_ENGINES,
} from "vue-chess-shared/constants";
import firebase from "firebase/app";
import { firestore, database } from "@/services/firebase";
import { mapGetters } from "vuex";
import {
  MATCH_DEFAULT,
  SIMUL_PRIVACY,
  CHALLENGE_TYPE,
  CHALLENGE_STATUS,
} from "@/constants";
import SimulPlayersTable from "@/components/simuls/SimulPlayersTable.vue";
import { toAiProfile } from "vue-chess-shared/utils/ai-helper";
import joinOrOpenMatchMixin from "@/mixins/joinOrOpenMatchMixin";
import SimulTable from "@/components/simuls/SimulTable.vue";
import AddOrInvitePlayers from "@/components/players/AddOrInvitePlayers.vue";

const simuls = firestore.collection("simuls");

export default {
  components: { SimulPlayersTable, SimulTable, AddOrInvitePlayers },

  name: "Simul",

  title() {
    return "Simul";
  },

  data() {
    return {
      isLoading: true,
      engines: CHESS_ENGINES,
      simul: null,
      isOwner: false,
      aiEngine: CHESS_ENGINE.STOCKFISH,
      aiLevel: 1,
    };
  },

  mixins: [joinOrOpenMatchMixin],

  watch: {
    currentPlayer: {
      immediate: true,
      handler: _.debounce(async function() {
        if (!this.currentPlayer) return;

        await this.$bind("simul", simuls.doc(this.simulId));
        this.isOwner =
          this.currentPlayer.username === this.simul.owner.username;

        // can only access a private simul when user is owner or joined the simul
        if (
          this.simul.privacy === SIMUL_PRIVACY.PRIVATE &&
          !this.isOwner &&
          !this.isJoined
        ) {
          this.$router.replace({
            name: "simuls",
          });
          this.$buefy.toast.open({
            message: "This is an invite only simul",
            type: "is-warning",
          });
          return;
        }

        if (
          this.simul.status !== SIMUL_STATUS.CREATED &&
          this.simul.staus !== SIMUL_STATUS.CLOSED &&
          this.isOwner
        ) {
          // go to show all games to play
          this.$router.replace({
            name: "simulGames",
            params: { simulId: this.simulId },
          });
        }

        this.isLoading = false;
      }, 1000),
    },

    simul: {
      handler: _.debounce(function(newVal, oldVal) {
        if (!newVal || !oldVal) return;

        if (newVal.status === oldVal.status) return;

        // if i'm openning a started simul, go to play screen
        if (
          newVal.status !== SIMUL_STATUS.CREATED &&
          newVal.status !== SIMUL_STATUS.CLOSED &&
          this.isOwner &&
          this.$route.name !== "simulGames"
        ) {
          this.$router.replace({
            name: "simulGames",
            params: { simulId: this.simulId },
          });
        }
      }, 1000),
    },

    permission() {
      if (this.permission.admin || this.permission.hostSimul) {
        return;
      }

      this.$router.push({
        name: "simuls",
      });
    },
  },

  computed: {
    ...mapGetters({
      currentPlayer: "getPlayer",
      permission: "getPermission",
    }),

    simulId() {
      return this.$route.params.simulId;
    },

    playerIds() {
      return (this.simul.players || []).map((p) => p.user_id);
    },

    canStart() {
      if (!this.simul) return false;
      return (
        this.simul.players &&
        this.simul.players.length >= 2 &&
        this.simul.status === SIMUL_STATUS.CREATED
      );
    },

    canUpdate() {
      if (!this.simul) return false;
      return this.simul.status === SIMUL_STATUS.CREATED;
    },

    canClose() {
      if (!this.simul) return false;
      return this.simul.status !== SIMUL_STATUS.FINISHED;
    },

    canAddPlayer() {
      if (!this.simul) return false;

      return (
        this.simul.status === SIMUL_STATUS.CREATED &&
        (!this.simul.players ||
          this.simul.players.length < this.simul.playerLimit)
      );
    },

    isOpen() {
      return this.simul.status === SIMUL_STATUS.CREATED;
    },

    isMine() {
      return this.simul.owner.user_id === this.currentPlayer.user_id;
    },

    isJoined() {
      return (
        this.simul.players &&
        !!this.simul.players.find(
          (p) => p.user_id === this.currentPlayer.user_id
        )
      );
    },
  },

  methods: {
    async startSimul() {
      this.isLoading = true;

      const promises = this.simul.players.map((p) => {
        return this.openMatch(
          p,
          this.simul.baseTimeControl * 60,
          MATCH_DEFAULT.INCREMENT,
          MATCH_DEFAULT.VARIANT,
          p.aiLevel ? GAME_OVERRIDE.PVAI : GAME_OVERRIDE.PVP,
          this.simulId
        );
      });

      await Promise.all(promises);

      await simuls.doc(this.simulId).update({
        status: SIMUL_STATUS.STARTED,
      });

      this.isLoading = false;
    },

    updateSimul() {
      this.$router.push({
        name: "simulEdit",
        params: { simulId: this.simulId },
      });
    },

    async closeSimul() {
      this.$buefy.dialog.confirm({
        title: "Closing simul?",
        message:
          "Are you sure you want to <b>close</b> this simul? This action cannot be undone.",
        confirmText: "Close",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          this.isLoading = true;
          await simuls.doc(this.simulId).update({
            status: SIMUL_STATUS.CLOSED,
          });
          this.$router.replace({ name: "simuls" });
          this.$buefy.toast.open("Simul closed!");
          this.isLoading = false;
        },
      });
    },

    async addAiPlayer() {
      if (!this.canAddPlayer) {
        return;
      }

      if (!this.aiEngine || !this.aiLevel) {
        return;
      }

      const aiPlayer = toAiProfile(this.aiEngine, this.aiLevel);

      await simuls.doc(this.simulId).update({
        players: firebase.firestore.FieldValue.arrayUnion(aiPlayer),
      });
    },

    async addPlayers() {
      this.$refs.addPlayers.open();
    },

    async invitePlayer(player) {
      if (!player || !player.user_id || player.challenge) return;

      const roomPlayerRef = database.ref(
        `rooms/lobby/players/${player.user_id}`
      );

      await roomPlayerRef.update({
        challenge: {
          type: CHALLENGE_TYPE.SIMUL,
          status: CHALLENGE_STATUS.SENT,
          simulId: this.simulId,
          player: {
            ...this.currentPlayer, // host
            timeOffset: parseFloat(
              sessionStorage.getItem("firestoreTimeOffset")
            ),
          },
          createdAt: firebase.database.ServerValue.TIMESTAMP,
        },
      });
    },

    async addPlayer(player) {
      if (!player || !player.user_id) return;

      // join the simul
      await firestore
        .collection("simuls")
        .doc(this.simulId)
        .update({
          players: firebase.firestore.FieldValue.arrayUnion(player),
        });
    },
  },
};
</script>

<style scoped>
.simul__games {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  margin-left: auto;
  margin-right: auto;
}

.simul__board--live {
  border: 2px solid green;
  width: 360px;
}

.simul__board--finished {
  border: 2px solid red;
  width: 360px;
}

.simul__board__close {
  position: absolute;
  top: 0;
  right: 50px;
  width: 40px;
  text-align: center;
  border: none;
}

.button .icon:first-child:not(:last-child) {
  margin: auto;
}

.b-checkbox > .button {
  border: none;
}

.notification {
  padding: 8px;
}

.notification /deep/ .media-left {
  margin-right: 8px;
}
</style>
